import React, { useState, useRef, useEffect } from "react";

type Card = 1 | 2 | 3 | 4 | 5 | 6 | 7;

interface RainDrop {
  id: number;
  left: number;
  delay: number;
}

const App: React.FC = () => {
  const [currentCard, setCurrentCard] = useState<Card>(1);
  const [volumeEnabled, setVolumeEnabled] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [popupContent, setPopupContent] = useState<"emoji" | "image">("emoji");
  const [popupImage, setPopupImage] = useState("");
  const [popupText, setPopupText] = useState("");
  const [popupEmoji, setPopupEmoji] = useState("");
  const [redScreen, setRedScreen] = useState(false);
  const [yesCount, setYesCount] = useState(7);
  const [buttonText, setButtonText] = useState("Ki");
  const [secondYesVisible, setSecondYesVisible] = useState(true);
  const [rainDrops, setRainDrops] = useState<RainDrop[]>([]);
  const [rainActive, setRainActive] = useState(false);
  const [rainSpeed, setRainSpeed] = useState(3);
  const [stopClickCount, setStopClickCount] = useState(0);

  const audioRef = useRef<HTMLAudioElement | null>(null);

  const playAudio = (file: string) => {
    if (volumeEnabled && audioRef.current) {
      audioRef.current.src = file;
      audioRef.current.play().catch((e) => console.log("Audio play error:", e));
    }
  };

  const showPopupWithContent = (
    type: "emoji" | "image",
    content: string,
    text = "",
    audio = "1.mp3",
  ) => {
    setPopupContent(type);
    if (type === "emoji") {
      setPopupEmoji(content);
    } else {
      setPopupImage(content);
    }
    setPopupText(text);
    setShowPopup(true);
    playAudio(audio);
  };

  const closePopup = () => {
    setShowPopup(false);
    setPopupText("");
    setPopupEmoji("");
    setPopupImage("");
  };

  const handleCard1Continue = () => {
    playAudio("3.mp3");
    const newCount = clickCount + 1;
    setClickCount(newCount);
    if (newCount >= 2) {
      setCurrentCard(2);
      setClickCount(0);
    }
  };

  const handleCard2Answer = (answer: "yes" | "no") => {
    if (answer === "yes") {
      showPopupWithContent("image", "7.jpg");
      setTimeout(() => {
        closePopup();
        setCurrentCard(3);
      }, 2000);
    } else {
      showPopupWithContent("emoji", "👍🏿");
      setTimeout(() => {
        closePopup();
        setCurrentCard(3);
      }, 2000);
    }
  };

  const handleCard3Answer = (answer: "today" | "tomorrow" | "never") => {
    if (answer === "today") {
      setCurrentCard(4);
    } else if (answer === "tomorrow") {
      setRedScreen(true);
      playAudio("2.mp3");
      setTimeout(() => {
        setRedScreen(false);
        setCurrentCard(5);
      }, 3000);
    } else {
      showPopupWithContent("image", "2.jpg", "Wrong Again");
      setTimeout(() => {
        closePopup();
        setCurrentCard(5);
      }, 2000);
    }
  };

  const handleCard4Answer = (answer: "today" | "tomorrow" | "never") => {
    if (answer === "today") {
      showPopupWithContent("emoji", "🎉");
      setTimeout(() => {
        closePopup();
        setCurrentCard(5);
      }, 2000);
    } else if (answer === "tomorrow") {
      window.location.href = "https://www.youtube.com/watch?v=dQw4w9WgXcQ";
    } else {
      showPopupWithContent("image", "2.jpg", "", "3.mp3");
      setTimeout(() => {
        closePopup();
        setCurrentCard(5);
      }, 2000);
    }
  };

  const handleCard5Yes = () => {
    // SECOND ROUND REACHED 1 → continue
    if (yesCount === 1 && secondYesVisible) {
      showPopupWithContent("image", "3.jpg", "", "2.mp3");
      setTimeout(() => {
        closePopup();
        setCurrentCard(6);
      }, 2000);
      return;
    }
  
    // FIRST ROUND REACHED 1 → reset to 7
    if (yesCount === 1 && !secondYesVisible) {
      setButtonText("Try 6 or 7 times");
      setYesCount(7);
      setSecondYesVisible(true);   // mark that we're now in the second round
      return;
    }
  
    // NORMAL COUNTDOWN (both rounds)
    if (yesCount > 1) {
      const newCount = yesCount - 1;
      setYesCount(newCount);
      setButtonText(`${newCount}jerac`);
      return;
    }
  };


  const handleCard6Answer = (answer: "ok" | "thanks") => {
    if (answer === "ok") {
      showPopupWithContent("image", "2.jpg", "", "3.mp3");
      setTimeout(() => {
        closePopup();
        setCurrentCard(7);
      }, 2000);
    } else {
      showPopupWithContent("emoji", "👍", "", "1.mp3");
      setTimeout(() => {
        closePopup();
        setCurrentCard(7);
      }, 2000);
    }
  };

  const startRain = () => {
    setRainActive(true);
    playAudio('4.mp3');
    const drops: RainDrop[] = [];
    for (let i = 0; i < 20; i++) {
      drops.push({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 2,
      });
    }
    setRainDrops(drops);
  };

  const handleStopRain = () => {
    const newCount = stopClickCount + 1;
    setStopClickCount(newCount);
    playAudio('3.mp3');
    if (newCount >= 10) {
      setRainActive(false);
      setStopClickCount(0);
      setRainSpeed(3);
    } else {
      setRainSpeed(rainSpeed * 0.7);
      const drops: RainDrop[] = [];
      const dropCount = 20 + newCount * 5;
      for (let i = 0; i < dropCount; i++) {
        drops.push({
          id: i,
          left: Math.random() * 100,
          delay: Math.random() * 2,
        });
      }
      setRainDrops(drops);
    }
  };

  return (
    <div className="min-h-screen bg-hero bg-repeat animate-bg-scroll flex items-center justify-center p-4 relative overflow-hidden">
      <audio ref={audioRef} />

      {redScreen && <div className="fixed inset-0 bg-red-600 z-50" />}

      {rainActive && (
        <div className="fixed inset-0 pointer-events-none z-30">
          {rainDrops.map((drop) => (
            <img
              key={drop.id}
              src="9.jpg"
              alt="rain"
              className="absolute w-12 h-12 animate-fall"
              style={{
                left: `${drop.left}%`,
                animationDelay: `${drop.delay}s`,
                animationDuration: `${rainSpeed}s`,
              }}
            />
          ))}
        </div>
      )}

      {showPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center">
            {popupContent === "emoji" ? (
              <div className="text-9xl mb-4">{popupEmoji}</div>
            ) : (
              <img
                src={popupImage}
                alt="popup"
                className="w-full h-64 object-contain mb-4"
              />
            )}
            {popupText && (
              <p className="text-2xl font-bold text-gray-800">{popupText}</p>
            )}
          </div>
        </div>
      )}

      <div className={`${currentCard === 6 ? "bg-none" : "bg-white"} rounded-3xl shadow-2xl p-8 max-w-md w-full`}>
        {currentCard === 1 && (
          <div className="text-center space-y-6">
            <h1 className="text-3xl font-bold text-gray-800">🎂 GILOCAV</h1>
            {!volumeEnabled ? (
              <button
                onClick={() => setVolumeEnabled(true)}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-4 px-6 rounded-xl text-xl transition"
              >
                🔊 Auwie Xmas
              </button>
            ) : (
              <button
                onClick={handleCard1Continue}
                className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-6 px-8 rounded-xl text-2xl transition"
              >
                Gadaushvi{clickCount == 1 && "tko"}
              </button>
            )}
          </div>
        )}

        {currentCard === 2 && (
          <div className="text-center space-y-6">
            <h2 className="text-2xl font-bold text-gray-800">
              Dabadebis dge gak?
            </h2>
            <p className="text-xl text-gray-600"></p>
            <div className="space-x-4 flex">
              <button
                onClick={() => handleCard2Answer("yes")}
                className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-4 px-6 rounded-xl text-xl transition"
              >
                Ki
              </button>
              <button
                onClick={() => handleCard2Answer("no")}
                className="w-full bg-red-500 hover:bg-red-600 text-white font-bold py-4 px-6 rounded-xl text-xl transition"
              >
                Ara
              </button>
            </div>
          </div>
        )}

        {currentCard === 3 && (
          <div className="text-center space-y-6">
            <h2 className="text-2xl font-bold text-gray-800">
              Rodis gak dabadebisdge 🤣
            </h2>
            <div className="space-x-4 flex">
              <button
                onClick={() => handleCard3Answer("today")}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-4 px-6 rounded-xl text-xl transition"
              >
                Dges
              </button>
              <button
                onClick={() => handleCard3Answer("tomorrow")}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-4 px-6 rounded-xl text-xl transition"
              >
                Xval
              </button>
              <button
                onClick={() => handleCard3Answer("never")}
                className="w-full bg-gray-500 hover:bg-gray-600 text-white font-bold py-4 px-6 rounded-xl text-xl transition"
              >
                Armaktko
              </button>
            </div>
          </div>
        )}

        {currentCard === 4 && (
          <div className="text-center space-y-6">
            <h2 className="text-2xl font-bold text-gray-800">
              Rodis gak dabadebisdge 🤣
            </h2>
            <p className="text-xl text-red-500">ARASWORIA, TAVIDAN CADE</p>
            <div className="space-y-4">
              <button
                onClick={() => handleCard4Answer("today")}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-4 px-6 rounded-xl text-xl transition"
              >
                Dges
              </button>
              <button
                onClick={() => handleCard4Answer("tomorrow")}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-4 px-6 rounded-xl text-xl transition"
              >
                Xval
              </button>
              <button
                onClick={() => handleCard4Answer("never")}
                className="w-full bg-gray-500 hover:bg-gray-600 text-white font-bold py-4 px-6 rounded-xl text-xl transition"
              >
                Agar Minda
              </button>
            </div>
          </div>
        )}

        {currentCard === 5 && (
          <div className="text-center space-y-6">
            <h2 className="text-2xl font-bold text-gray-800">Mogiloco?</h2>
            <div className="space-x-4 flex">
              <button
                onClick={handleCard5Yes}
                className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-4 px-6 rounded-xl text-xl transition"
              >
                {buttonText}
              </button>
              {secondYesVisible && (
                <button
                  onClick={handleCard5Yes}
                  className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-4 px-6 rounded-xl text-xl transition"
                >
                  Ki
                </button>
              )}
            </div>
          </div>
        )}

        {currentCard === 6 && (
          <div className="text-center space-y-6 relative">
            <div className="relative z-10">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                🎉 GILOCAV 🎉
              </h2>
              <p className="text-xl text-gray-600 mb-6">
                GILOCAV DABADEBIS DGES DA SUL BEDNIERI DA YVELAZE MAGARI (takoze
                magari ver iknebi verasdros) IYAVI (verc VAKGTHANGIDZE) DA
                BEDNIERI (2jer vtkvi), GILOCAV
              </p>
              <div className="space-y-4">
                <button
                  onClick={() => handleCard6Answer("ok")}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-4 px-6 rounded-xl text-xl transition"
                >
                  OK
                </button>
                <button
                  onClick={() => handleCard6Answer("thanks")}
                  className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold py-4 px-6 rounded-xl text-xl transition"
                >
                  MADLOBA
                </button>
              </div>
            </div>
          </div>
        )}

        {currentCard === 7 && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-800 text-center">
              Bonus (DELUXE EDITION)
            </h2>
            <div className="grid grid-cols-3 gap-2">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <img
                  key={i}
                  src={`${i}.jpg`}
                  alt={`img${i}`}
                  className="w-full h-24 object-cover rounded-lg"
                />
              ))}
              <img
                src="7.jpg"
                alt="img7"
                className="w-full h-24 object-cover rounded-lg animate-pulse"
              />
            </div>
            <div className="space-y-4">
              <div>
                <p className="font-semibold mb-2">Ramdeni Suratia</p>
                <div className="grid grid-cols-2 gap-2">
                  <button className="bg-gray-200 hover:bg-gray-300 py-2 rounded-lg">
                    41
                  </button>
                  <button className="bg-gray-200 hover:bg-gray-300 py-2 rounded-lg">
                    6
                  </button>
                  <button className="bg-gray-200 hover:bg-gray-300 py-2 rounded-lg">
                    7
                  </button>
                  <button className="bg-gray-200 hover:bg-gray-300 py-2 rounded-lg">
                    6-7
                  </button>
                </div>
              </div>
              <div>
                <p className="font-semibold mb-2">Romelia Elenes Fav?</p>
                <div className="grid grid-cols-4 gap-2">
                  {[1, 2, 3, 3, 4, 5, 6, 7].map((num, idx) => (
                    <button
                      key={idx}
                      className="bg-gray-200 hover:bg-gray-300 py-2 rounded-lg"
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {!rainActive ? (
              <div className="flex flex-col items-center gap-y-2">
                <div className="w-full text-center font-xl">THE END</div>
                <button
                  onClick={startRain}
                  className="bg-red-600 hover:bg-red-700 text-white font-bold py-6 px-6 rounded-full text-xl transition h-32 w-32"
                >
                  DO NOT CLICK
                </button>
              </div>
            ) : (
              <button
                onClick={handleStopRain}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-6 px-6 rounded-full text-xl transition z-40 relative"
              >
                STOP
              </button>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes fall {
          0% {
            transform: translateY(-100px);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh);
            opacity: 0.5;
          }
        }
        .animate-fall {
          animation: fall linear infinite;
        }
      `}</style>
    </div>
  );
};

export default App;
